import React, { useEffect, useState } from 'react';
import { LogIn, Loader2, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SESSION_EXPIRED_EVENT, SESSION_RESTORED_EVENT } from '../../services/sessionEvents';

/**
 * The session ran out while the user was working (the refresh token was gone —
 * e.g. the server restarted): ask for the password right here, so the page and
 * whatever was being drawn stay as they are, instead of silently failing every
 * request. After a successful login the page is told (SESSION_RESTORED_EVENT) so
 * it can retry what failed (the custom graph re-saves).
 */
const SessionExpiredModal: React.FC = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onExpired = () => { setOpen(true); setError(null); };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);
  useEffect(() => { if (open && user?.username) setUsername(user.username); }, [open, user?.username]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password);
      setPassword('');
      setOpen(false);
      window.dispatchEvent(new Event(SESSION_RESTORED_EVENT));
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Нэвтэрч чадсангүй — нэр, нууц үгээ шалгана уу');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sx-backdrop" role="dialog" aria-modal="true" aria-labelledby="sx-title">
      <form className="sx-box" onSubmit={submit}>
        <div className="sx-head">
          <ShieldAlert size={20} />
          <h3 id="sx-title">Нэвтрэлтийн хугацаа дууссан</h3>
        </div>
        <p className="sx-text">
          Ажлаа алдахгүйн тулд энд дахин нэвтэрнэ үү — хуудас, зурж байсан зүйл чинь хэвээр үлдэнэ.
          Нэвтэрсний дараа амжилтгүй болсон хадгалалт дахин хийгдэнэ.
        </p>
        <label className="sx-field">
          <span>Нэвтрэх нэр</span>
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" readOnly={!!user?.username} />
        </label>
        <label className="sx-field">
          <span>Нууц үг</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus />
        </label>
        {error && <div className="sx-error" role="alert">{error}</div>}
        <div className="sx-actions">
          <button type="button" className="sx-ghost"
            onClick={() => { if (window.confirm('Нэвтрэх хуудас руу шилжвэл хадгалаагүй ажил алга болж магадгүй. Үргэлжлүүлэх үү?')) { setOpen(false); navigate('/login'); } }}>
            Нэвтрэх хуудас
          </button>
          <button type="submit" className="sx-primary" disabled={busy || !password}>
            {busy ? <Loader2 size={14} className="spinner" /> : <LogIn size={14} />} Нэвтрэх
          </button>
        </div>
      </form>
    </div>
  );
};

export default SessionExpiredModal;
